import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Realtime client: presence, advisory edit locks, and live catalog refresh.
 *
 * Mirrors backend/realtime.js. This is not collaborative co-editing -- there is
 * no shared document and no merging. It exists so two people cannot silently
 * overwrite each other's work.
 *
 * Every feature degrades to the previous behaviour when the socket is down:
 * no presence, no lock, and the editor stays writable.
 */

export type Viewer = { userId: string; username: string };
export type LockHolder = { userId: string; username: string };
export type RealtimeStatus = "connecting" | "open" | "closed";

type ServerMessage =
  | { t: "ready"; user: Viewer & { role: string } }
  | { t: "presence"; path: string; viewers: Viewer[]; lock: LockHolder | null }
  | { t: "catalog"; by: string | null }
  | { t: "saved"; path: string; by: string | null }
  | { t: "lock-denied"; path: string; holder: LockHolder | null }
  | { t: "lock-lost"; path: string; by: string }
  | { t: "error"; code?: string; message: string }
  | { t: "pong" };

type RealtimeApi = {
  status: RealtimeStatus;
  /** Current user id, once authenticated. */
  selfId: string | null;
  /** Everyone viewing the watched path, including you. */
  viewers: Viewer[];
  /** Who holds the edit lock on the watched path, if anyone. */
  lock: LockHolder | null;
  /** True only when someone *else* holds the lock. */
  lockedByOther: boolean;
  /** Set (or clear, with null) the diagram this client is watching. */
  watch: (path: string | null) => void;
  requestLock: () => void;
  releaseLock: () => void;
  takeover: () => void;
  /** Subscribe to catalog changes made by anyone. Returns an unsubscribe fn. */
  onCatalogChange: (handler: (by: string | null) => void) => () => void;
  /** Subscribe to saves of any watched diagram. Returns an unsubscribe fn. */
  onDiagramSaved: (handler: (path: string, by: string | null) => void) => () => void;
};

const RealtimeContext = createContext<RealtimeApi | null>(null);

export function useRealtime(): RealtimeApi {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error("useRealtime must be used within a <RealtimeProvider>");
  return ctx;
}

const HEARTBEAT_MS = 25_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 20_000;

function socketUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/api/realtime`;
}

export function RealtimeProvider({
  token,
  children,
}: {
  token: string;
  children: React.ReactNode;
}) {
  const [status, setStatus] = useState<RealtimeStatus>("closed");
  const [selfId, setSelfId] = useState<string | null>(null);
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [lock, setLock] = useState<LockHolder | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const watchedPathRef = useRef<string | null>(null);
  // Whether this client wants the lock, so it can be re-claimed after a
  // reconnect without the editor having to notice the drop.
  const wantsLockRef = useRef(false);

  const catalogHandlers = useRef(new Set<(by: string | null) => void>());
  const savedHandlers = useRef(new Set<(path: string, by: string | null) => void>());

  const send = useCallback((payload: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  /* --- Socket lifecycle --------------------------------------------------- */

  // One effect owns the socket, the reconnect timer and the heartbeat. Keeping
  // them together avoids the circular dependency you get from splitting
  // connect/reconnect into separate useCallbacks.
  useEffect(() => {
    if (!token) return;

    let disposed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let attempts = 0;

    const open = () => {
      if (disposed) return;
      setStatus("connecting");

      try {
        socket = new WebSocket(socketUrl());
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = socket;

      socket.onopen = () => {
        attempts = 0;
        // The token travels in the first frame rather than the URL, so it never
        // reaches proxy or access logs.
        socket?.send(JSON.stringify({ t: "auth", token }));
      };

      socket.onmessage = (event) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }

        switch (msg.t) {
          case "ready":
            setStatus("open");
            setSelfId(String(msg.user.userId));
            // Restore what this client was doing before the connection dropped.
            if (watchedPathRef.current) {
              socket?.send(JSON.stringify({ t: "watch", path: watchedPathRef.current }));
              if (wantsLockRef.current) socket?.send(JSON.stringify({ t: "lock" }));
            }
            break;

          case "presence":
            // Late frames for a diagram we already navigated away from.
            if (msg.path !== watchedPathRef.current) break;
            setViewers(msg.viewers);
            setLock(msg.lock);
            break;

          // Destructured first: `msg` is a mutable binding, so TypeScript drops
          // the discriminated-union narrowing inside the callbacks below.
          case "catalog": {
            const { by } = msg;
            catalogHandlers.current.forEach((handler) => handler(by));
            break;
          }

          case "saved": {
            const { path, by } = msg;
            savedHandlers.current.forEach((handler) => handler(path, by));
            break;
          }

          case "lock-denied":
            wantsLockRef.current = false;
            setLock(msg.holder);
            break;

          case "lock-lost":
            // Someone took over. Drop to read-only rather than fight for it.
            wantsLockRef.current = false;
            break;

          case "error":
            console.warn("[realtime]", msg.message);
            break;

          default:
            break;
        }
      };

      socket.onclose = () => {
        wsRef.current = null;
        socket = null;
        setStatus("closed");
        setViewers([]);
        setLock(null);
        scheduleReconnect();
      };

      // onclose always follows onerror, so reconnect is handled in one place.
      socket.onerror = () => undefined;
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== null) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);
      attempts += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        open();
      }, delay);
    };

    open();

    const heartbeat = window.setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: "ping" }));
    }, HEARTBEAT_MS);

    return () => {
      disposed = true;
      window.clearInterval(heartbeat);
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      // Detach onclose first, or teardown schedules a reconnect on the way out.
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
      wsRef.current = null;
    };
  }, [token]);

  /* --- Public API --------------------------------------------------------- */

  const watch = useCallback(
    (path: string | null) => {
      if (watchedPathRef.current === path) return;
      watchedPathRef.current = path;
      wantsLockRef.current = false;
      setViewers([]);
      setLock(null);
      send({ t: "watch", path });
    },
    [send],
  );

  const requestLock = useCallback(() => {
    wantsLockRef.current = true;
    send({ t: "lock" });
  }, [send]);

  const releaseLock = useCallback(() => {
    wantsLockRef.current = false;
    send({ t: "unlock" });
  }, [send]);

  const takeover = useCallback(() => {
    wantsLockRef.current = true;
    send({ t: "takeover" });
  }, [send]);

  const onCatalogChange = useCallback((handler: (by: string | null) => void) => {
    const handlers = catalogHandlers.current;
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  }, []);

  const onDiagramSaved = useCallback(
    (handler: (path: string, by: string | null) => void) => {
      const handlers = savedHandlers.current;
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },
    [],
  );

  const lockedByOther = Boolean(lock && selfId && String(lock.userId) !== selfId);

  const api = useMemo<RealtimeApi>(
    () => ({
      status,
      selfId,
      viewers,
      lock,
      lockedByOther,
      watch,
      requestLock,
      releaseLock,
      takeover,
      onCatalogChange,
      onDiagramSaved,
    }),
    [
      status,
      selfId,
      viewers,
      lock,
      lockedByOther,
      watch,
      requestLock,
      releaseLock,
      takeover,
      onCatalogChange,
      onDiagramSaved,
    ],
  );

  return <RealtimeContext.Provider value={api}>{children}</RealtimeContext.Provider>;
}
